import type { Session } from '@supabase/supabase-js';
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { isSupabaseConfigured, supabase } from './client';

type SignInResult = { ok: true } | { ok: false; error: string };

type AuthContextValue = {
  /** Active Supabase session, or null when signed out / not configured. */
  session: Session | null;
  isAuthenticated: boolean;
  email: string | null;
  /** True when env vars are set. UI should hide auth affordances otherwise. */
  configured: boolean;
  /** Send a magic link to the given email. Resolves with { ok, error? }. */
  signInWithMagicLink: (email: string) => Promise<SignInResult>;
  signOut: () => Promise<void>;
  /** Open the SignInSheet (rendered by AuthProvider). */
  openSignIn: () => void;
  /** Close the SignInSheet. */
  closeSignIn: () => void;
  /** True while the SignInSheet is open. */
  signInOpen: boolean;
};

const AuthContext = createContext<AuthContextValue | null>(null);

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error('useAuth must be used inside <AuthProvider>');
  }
  return ctx;
}

/**
 * Owns the Supabase session and the SignInSheet open state. Subscribes
 * to onAuthStateChange so any tab signing in / signing out updates the
 * UI immediately. When Supabase isn't configured, the provider still
 * mounts but every method becomes a no-op so consumers don't blow up.
 */
export function AuthProvider({ children }: { children: ReactNode }) {
  const [session, setSession] = useState<Session | null>(null);
  const [signInOpen, setSignInOpen] = useState(false);
  const configured = isSupabaseConfigured();

  // Hydrate the existing session on mount and subscribe to changes.
  useEffect(() => {
    if (!supabase) return;
    let cancelled = false;
    void supabase.auth.getSession().then(({ data }) => {
      if (!cancelled) setSession(data.session);
    });
    const { data: sub } = supabase.auth.onAuthStateChange((_event, next) => {
      setSession(next);
    });
    return () => {
      cancelled = true;
      sub.subscription.unsubscribe();
    };
  }, []);

  const signInWithMagicLink = useCallback(
    async (email: string): Promise<SignInResult> => {
      if (!supabase) {
        return { ok: false, error: 'Sign-in is not configured on this build.' };
      }
      const trimmed = email.trim().toLowerCase();
      if (!trimmed) return { ok: false, error: 'Enter an email address.' };
      const { error } = await supabase.auth.signInWithOtp({
        email: trimmed,
        options: {
          // Bring the user back to the app after they tap the link. The
          // SDK detects the session in the URL on return.
          emailRedirectTo:
            typeof window !== 'undefined' ? window.location.origin : undefined,
        },
      });
      if (error) return { ok: false, error: error.message };
      return { ok: true };
    },
    [],
  );

  const signOut = useCallback(async () => {
    if (!supabase) return;
    await supabase.auth.signOut();
    setSession(null);
  }, []);

  const openSignIn = useCallback(() => setSignInOpen(true), []);
  const closeSignIn = useCallback(() => setSignInOpen(false), []);

  const value = useMemo<AuthContextValue>(
    () => ({
      session,
      isAuthenticated: !!session,
      email: session?.user.email ?? null,
      configured,
      signInWithMagicLink,
      signOut,
      openSignIn,
      closeSignIn,
      signInOpen,
    }),
    [
      session,
      configured,
      signInWithMagicLink,
      signOut,
      openSignIn,
      closeSignIn,
      signInOpen,
    ],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
